/**
 * Types du Test de qualification des décideurs de La Régie.
 *
 * Source de vérité : le contrat d'API donné par la mission (backend
 * `regie_test_items` / `regie_test_candidates` / `regie_test_answers`, cf.
 * note DOZKA « La Régie — Test de qualification des décideurs »). Ce module
 * ne fait AUCUN appel réseau — uniquement des formes de données, côté
 * candidat (public, jeton dans l'URL) et côté admin (Kayla, sous WorkOS).
 *
 * Note sécurité : `Card.kind` (taxonomie serveur clean|fault|time_waster|
 * sale_2stage) encode presque le geste attendu → le backend NE l'envoie PAS
 * au candidat (retiré de la whitelist `sanitize.py`). Le champ reste optionnel
 * ici uniquement pour les fixtures du mode maquette ; aucun composant candidat
 * ne le lit (la vente 2 étages se déduit de la présence prix/média).
 */

// ─── Vocabulaire partagé jours / créneaux ──────────────────────────────────

export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const DAY_KEYS: readonly DayKey[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

/** Les 4 VRAIS shifts de La Régie (cf. `constants.ts` → `SHIFTS`, source de
 *  vérité `cockpit/panels/organisation/shiftsFixes.ts`). La lettre stockée
 *  est INDÉPENDANTE du fuseau — seul l'affichage des heures se localise. */
export type SlotKey = "a" | "b" | "c" | "d";

/** Un créneau tel qu'il a été AFFICHÉ à un candidat, figé avec ses cases.
 *
 *  🩸 Les lettres de `Availability` ne portent pas leur sens : le 18-08-2026
 *  la grille a changé deux fois dans la soirée et un « dispo le soir » (`c`)
 *  s'est relu « dispo l'après-midi ». Le serveur garde donc la grille vue à
 *  côté des cases cochées, et la fiche affiche CES heures-là.
 *
 *  Structurellement identique à `ShiftDef` (constants.ts) — déclaré ici pour
 *  ne pas faire dépendre `types` de `constants` (cycle d'import). */
export interface ShiftVu {
  key: SlotKey;
  letter: string;
  name: string;
  startMin: number;
  endMin: number;
}

export const SLOT_KEYS: readonly SlotKey[] = ["a", "b", "c", "d"] as const;

/** Grille de disponibilités : jour → liste des shifts cochés (lettres
 *  `a`..`d` — `e` et `f` ont existé du 18-08 au 12-09-2026 et traînent encore
 *  dans des fiches : une lettre inconnue s'ignore, cf. `constants.SHIFTS`). Un jour absent ou à liste vide veut dire « aucune dispo ce
 *  jour-là ». */
export type Availability = Partial<Record<DayKey, SlotKey[]>>;

// ─── Gestes & motifs (Guide du décideur) ───────────────────────────────────

export type Gesture = "send" | "correct" | "discard" | "reorient";

/** Les 5 motifs de correction — mêmes slugs que la prod (La Régie réelle,
 *  cf. `@/regie-test/correctionReasons.ts`), figés par le contrat backend
 *  du test. `other` n'a pas d'entrée dans `CORRECTION_REASONS` (prod) : on
 *  l'ajoute nous-mêmes en 5e option, cf. `constants.ts`. */
export type CorrectionMotif =
  | "off_topic"
  | "incoherent"
  | "invented"
  | "robotic_tone"
  | "other";

/** Motif fixe envoyé avec le geste Écarter (pas de picker — cf. contrat). */
export const DISCARD_MOTIF = "other" as const;

export type ReorientIntent = "continue" | "heat" | "sell";

// ─── Carte (candidat) ───────────────────────────────────────────────────────

export interface ThreadMessage {
  dir: "in" | "out";
  text: string;
}

export interface Card {
  id: string;
  /** Taxonomie serveur — NON envoyée au candidat (retirée côté backend) ;
   *  présente seulement dans les fixtures du mode maquette. */
  kind?: string;
  fan_name: string;
  creator_hint: string | null;
  thread: ThreadMessage[];
  proposal_message: string;
  proposal_price_cents: number | null;
  proposal_media_label: string | null;
  proposal_stage_label: string | null;
  is_warmup: boolean;
}

// ─── Endpoints candidat ─────────────────────────────────────────────────────

export type CandidateTestStatus = "created" | "started" | "submitted";

export interface CandidateStatusResponse {
  status: CandidateTestStatus;
  first_name: string | null;
  /** E-mail saisi au profil — sert à l'écran de fin (« invitation envoyée
   *  à … ») et, server-to-server, à l'auto-invitation Convex. Optionnel :
   *  absent tant que le backend n'expose pas le champ. */
  email?: string | null;
  has_profile: boolean;
  progress: { answered: number; total: number };
}

export interface LandingApplicationResponse {
  token: string;
  url: string;
}

/** Quand le candidat peut commencer (choix simple, remplace la date). */
export type StartAvailability = "now" | "this_week" | "later";
/** Situation actuelle du candidat (choix simple). */
export type WorkSituation = "free" | "student" | "employed";

/** Ce que Kayla veut savoir (18-08) : « si c'est un homme ou une femme ». */
export type Gender = "homme" | "femme";

export interface ProfileFormValues {
  first_name: string;
  last_name: string;
  gender: Gender;
  /** ISO `AAAA-MM-JJ`. 📅 La DATE, jamais l'âge : un âge saisi est faux un an
   *  plus tard et personne ne revient le corriger. L'âge se recalcule à la
   *  lecture, côté fiche. */
  birth_date: string;
  email: string;
  /** Le pseudo Discord — OPTIONNEL. C'est le canal de relance (serveur
   *  Cerbère), pas une condition de recevabilité : l'e-mail suffit à joindre
   *  un candidat, et rendre celui-ci obligatoire perdrait des leads. */
  contact_discord: string | null;
  timezone: string;
  availability: Availability;
  // Kayla fixe les heures (pas le candidat) → plus de `hours_per_week` ni de
  // date de début ; deux choix simples à la place.
  start_availability: StartAvailability;
  work_situation: WorkSituation;
  /** Temps passé sur l'écran Formation (ms). Jamais rempli au 1er POST (la
   *  formation vient APRÈS le profil) — envoyé via un RE-POST best-effort du
   *  profil à la fin de la formation : le backend backfille la session 1 si
   *  `consignes_ms` y est encore null (signal de rythme « soft »). */
  consignes_ms?: number;
}

export interface DeckPayload {
  warmup: Card[];
  cards: Card[];
}

/** Réponse de POST /{token}/profile — le backend GÈLE la session 1 et en
 *  renvoie l'`session_id` avec le deck. Le candidat travaille donc
 *  directement cette session (pas de POST /session redondant pour la 1re —
 *  `startSession` est réservé aux REPRISES). `session_index` vaut toujours 1
 *  ici (optionnel : défaut 1 si le backend ne le renvoie pas). */
export interface ProfileResponse extends DeckPayload {
  session_id: string;
  session_index?: number;
}

/** Réponse de GET /deck — comme DeckPayload + les items déjà répondus
 *  (reprise après coupure). Absente de la réponse POST /profile (tirage
 *  tout frais, rien de répondu). */
export interface DeckResponse extends DeckPayload {
  answered_item_ids: string[];
}

export interface AnswerPayload {
  item_id: string;
  /** Index de la carte dans son tableau (warmup ou test), tel que tiré côté
   *  serveur — sert à valider l'ordre/la position côté scoring. */
  position: number;
  is_warmup: boolean;
  gesture: Gesture;
  motif?: CorrectionMotif;
  corrected_text?: string;
  reorient_intent?: ReorientIntent;
  shown_at?: string;
  dwell_ms?: number;
}

/** Réponse de POST /{token}/answer — `AnswerAckResponse`.
 *
 *  🪤 Les trois clés sont TOUJOURS envoyées : hors warmup, `feedback` et
 *  `was_expected` valent `null`, ils ne sont pas absents. Discriminer par
 *  présence de clé (`"feedback" in res`) est donc toujours vrai — c'est la
 *  valeur qu'il faut regarder. */
export interface AnswerAckResponse {
  ok: boolean;
  feedback: string | null;
  was_expected: boolean | null;
}

/** Réponse de POST /{token}/device — `DeviceResponse`. Le serveur relit ce
 *  qu'il a stocké ; il n'y a PAS de `ok` dans cette réponse. */
export interface DeviceResponse {
  device_class: DeviceClass | null;
  computer_confirmed: boolean;
}

export interface SubmitResponse {
  first_name: string | null;
}

// ─── Endpoints admin ────────────────────────────────────────────────────────

/** Le résumé de score d'une ligne de leads — `ScoreSummary` côté serveur.
 *  🪤 TOUT y est nullable : un candidat qui n'a terminé aucune session n'a
 *  aucune de ces mesures, et le serveur envoie alors `null` champ par champ. */
export interface CandidateScore {
  fault_catch_rate: number | null; // fraction 0..1
  false_positives: number | null;
  motif_exact: number | null; // fraction 0..1
  dwell_median_ms: number | null;
}

/**
 * Une ligne de la table des leads (GET /candidates).
 *
 * ⚠️ Tout ce qui a été ajouté le 18-08 est OPTIONNEL, et ce n'est pas de la
 * prudence décorative : le cockpit se déploie AVANT Cyrano, donc pendant la
 * fenêtre de rebuild ces clés n'existent pas dans la réponse. Une colonne
 * vide, oui ; un écran blanc, non.
 */
export interface CandidateListItem {
  id: string;
  first_name: string | null;
  last_name: string | null;
  status: CandidateTestStatus;
  score: CandidateScore | null;
  /** Forme non garantie par le contrat — soit une chaîne déjà formatée par
   *  le serveur, soit une map jour→créneaux→compte. `format.ts` gère les
   *  deux formes défensivement. */
  availability_summary: unknown;
  /** La map COMPLÈTE {jour: [shifts]} — c'est elle, et pas le résumé, qui
   *  rend possible « montre-moi ceux qui peuvent tenir le shift B ». */
  availability?: Availability | null;
  /** La grille que CE candidat a vue. `null` = inconnue (coché avant qu'on
   *  l'enregistre) — surtout pas « celle d'aujourd'hui ». */
  availability_grid?: ShiftVu[] | null;
  gender?: Gender | null;
  birth_date?: string | null;
  /** L'âge RÉVOLU calculé par le serveur à la lecture. Jamais stocké : c'est
   *  ce qui le garde vrai après l'anniversaire du candidat. */
  age?: number | null;
  email?: string | null;
  contact_discord?: string | null;
  /** Le débit. Trier dessus se fait sur `rtt_median_ms` — le verdict n'a que
   *  trois valeurs et ne départage pas deux candidats de la même couleur. */
  connection?: {
    verdict: ConnVerdict | null;
    rtt_median_ms: number | null;
    rtt_p95_ms: number | null;
  } | null;
  device_class?: DeviceClass | null;
  computer_confirmed?: boolean;
  source?: string | null;
  source_detail?: string | null;
  created_at: string;
  started_at?: string | null;
  submitted_at?: string | null;
  /** Non-null = ÉCARTÉ par Kayla (12-09-2026). La table le range sous un pli
   *  au lieu de le montrer ; la fiche propose de le réintégrer. Optionnel :
   *  le cockpit se déploie avant Cyrano, la clé peut manquer un temps. */
  archived_at?: string | null;
}

export interface CreateCandidateResponse {
  id: string;
  token: string;
  url: string;
}

export interface CandidateProfileDetail {
  first_name: string;
  last_name: string;
  email: string;
  timezone: string;
  start_availability: StartAvailability | null;
  work_situation: WorkSituation | null;
  // Legacy (plus collectés) — restent optionnels pour compat.
  contact_telegram?: string | null;
  contact_whatsapp?: string | null;
  hours_per_week?: number | null;
  start_date?: string | null;
  /** Ajoutés le 18-08 — absents des candidatures d'avant, et absents de la
   *  réponse tant que Cyrano n'a pas fini son rebuild. */
  contact_discord?: string | null;
  gender?: Gender | null;
  birth_date?: string | null;
  age?: number | null;
}

export interface CandidateDetailCard {
  fan_name: string;
  stage_label: string | null;
  expected_gesture: Gesture;
  expected_motif: string | null;
  given_gesture: Gesture | null;
  given_motif: string | null;
  corrected_text: string | null;
  dwell_ms: number | null;
  is_correct: boolean;
  rationale: string | null;
}

/**
 * Détail admin — FICHE GRADUÉE (cf. maquette « Côté Kayla », CONTRACT §5).
 * `cards` est désormais la liste GRADUÉE de la DERNIÈRE session
 * (`GradedCard[]`, cf. plus bas) — remplace l'ancienne liste binaire
 * `CandidateDetailCard[]` (type conservé ci-dessus pour compat de lecture,
 * mais plus produit par aucun client de ce module). L'écran fiche admin est
 * entièrement refondu pour suivre la maquette (checklist 4 critères, courbe
 * de maîtrise, piliers, barème, cartes) — c'est l'agent écrans qui portera
 * `CandidateDetailDrawer.tsx` sur cette nouvelle forme.
 */
export interface CandidateDetail {
  /**
   * ⛔ Le serveur répond À PLAT (`first_name`, `email`, … à la racine) — il
   * n'a JAMAIS imbriqué sous `profile`. Ce champ était une invention du
   * cockpit : la fiche lisait `detail.profile.first_name`, ce qui ne pouvait
   * marcher qu'en `?mock=1` (les fixtures, elles, imbriquaient) et jetait un
   * TypeError sur la vraie réponse. Corrigé le 18-08 ; `profile` reste ici en
   * OPTIONNEL le temps que les fixtures migrent, et n'est plus lu par
   * personne. Cf. `CandidateFiche.reel.test.tsx`, qui rejoue le payload réel.
   */
  profile?: CandidateProfileDetail;
  first_name: string | null;
  last_name: string | null;
  email?: string | null;
  timezone?: string | null;
  start_availability?: StartAvailability | null;
  work_situation?: WorkSituation | null;
  contact_discord?: string | null;
  gender?: Gender | null;
  birth_date?: string | null;
  /** L'âge RÉVOLU, calculé par le serveur à la lecture. */
  age?: number | null;
  status?: CandidateTestStatus;
  /** Quand la candidature est arrivée — l'en-tête de la fiche le dit, comme la
   *  colonne « Reçu le » de la table. */
  created_at?: string;
  /** Non-null = écarté. Cf. `CandidateListItem.archived_at`. */
  archived_at?: string | null;
  /** `null` quand le candidat n'a pas rempli le formulaire — 18 liens sur 26
   *  sont dans ce cas en base le 18-08. */
  availability: Availability | null;
  /** La grille que CE candidat a vue, figée avec ses cases. `null` = inconnue :
   *  la fiche le DIT au lieu de lire ses lettres avec la grille du jour. */
  availability_grid?: ShiftVu[] | null;
  criteria: Criteria;
  verdict: Verdict;
  sessions: SessionPoint[];
  sessions_to_threshold: number | null;
  /** `null` tant qu'aucune session n'est allée au bout. */
  best_score: number | null;
  /**
   * ⛔ Un OBJET, pas une liste — c'est ce que rend `score_candidate` :
   * `{dangerous_sent: [n…], false_positives: [n…], improving}`, une entrée par
   * session. Le cockpit le typait `FaultLearning[]` : `.length` valait
   * `undefined`, donc le bloc « ce qu'il a appris » ne s'affichait JAMAIS. Pas
   * un crash — pire, un silence.
   */
  fault_learning: FaultLearningDto;
  flags: string[];
  rhythm: {
    /** Les trois durées sont NULLABLES côté serveur (`int | None`) : un
     *  candidat qui n'a pas fini n'a ni session complète ni médiane. */
    consignes_ms: number | null;
    /** Durée de la DERNIÈRE session complète. */
    session_ms: number | null;
    dwell_median_ms: number | null;
    /** Lecture qualitative FR ("A lu les consignes · rythme régulier."). */
    reading: string;
  };
  /** Cartes graduées de la DERNIÈRE session (pas tout l'historique). */
  cards: GradedCard[];
  /** L'épreuve de VENTE de la dernière session, quand c'est celle-là qu'il a
   *  passée. `null`/absent sur les candidatures d'avant le 18-08 (cartes de
   *  décision) : les deux formes cohabitent en base, la fiche rend l'une OU
   *  l'autre. Optionnel côté type — le cockpit se déploie AVANT Cyrano, donc
   *  le champ manque pendant la fenêtre de rebuild. */
  vente?: VenteResultat | null;
  device: {
    /**
     * ⛔ `cls`, PAS `class` — c'est le nom du champ dans `DeviceDto`. Le
     * cockpit lisait `detail.device.class`, toujours `undefined` : l'icône
     * tombait sur son défaut « 📱 » pour TOUT LE MONDE, ordinateurs compris.
     */
    cls: DeviceClass | "unknown";
    label: string;
    detail: string | null;
    /**
     * ⛔ Une CHAÎNE (« À confirmer »), pas un objet. Le cockpit lisait
     * `pill.cls` et `pill.txt` : `TONE[undefined].text` jetait un TypeError et
     * l'ErrorBoundary avalait la fiche entière — uniquement pour un candidat
     * qui n'est PAS sur ordinateur, d'où un bug qui attend le bon candidat.
     */
    pill: string | null;
  };
  connection: {
    /** `null` tant que la sonde n'a pas tourné. */
    verdict: ConnVerdict | null;
    rtt_median_ms: number | null;
    rtt_p95_ms: number | null;
    /** ⚠️ Le serveur ne l'envoie PAS (absent de `ConnectionDto`). */
    dropouts_pct?: number;
  };
}

// ─── Vérif technique (appareil / connexion) ────────────────────────────────

export type DeviceClass = "computer" | "mobile" | "tablet";

export interface DeviceInfo {
  device_class: DeviceClass;
  computer_confirmed: boolean;
}

export type ConnVerdict = "good" | "ok" | "bad";

/** Ce que le cockpit ENVOIE à POST /{token}/probe — `ProbeBody`. Sonde
 *  EXPLICITE (`phase:"probe"`, à l'étape 1) ou mesure PASSIVE
 *  (`phase:"passive"`, échantillonnée pendant le test). */
export interface ProbePayload {
  phase: "probe" | "passive";
  rtt_median_ms: number;
  rtt_p95_ms: number;
  dropouts_pct: number;
}

/** Ce que POST /{token}/probe REND — `ProbeResponse`, et ce n'est PAS la
 *  même forme que la requête.
 *
 *  🪤 Pas de `phase` en retour. Et les trois mesures sont nullables : le
 *  serveur se contente de ré-émettre ce qu'on lui a donné, et `ProbeBody` les
 *  déclare toutes optionnelles. Seul `verdict` est garanti — c'est lui que le
 *  serveur calcule. */
export interface ProbeResult {
  verdict: ConnVerdict;
  rtt_median_ms: number | null;
  rtt_p95_ms: number | null;
  dropouts_pct: number | null;
}

// ─── Sessions (maîtrise multi-session) ─────────────────────────────────────

export type SessionStatus = "in_progress" | "submitted";

/** Une session RÉELLE : tous ses champs sont renseignés. C'est ce que rend
 *  `startSession`, et ce qu'on obtient d'une `CurrentSession` une fois
 *  narrowée (`sessionOuverte`, dans la page candidat). */
export interface SessionInfo {
  session_id: string;
  session_index: number;
  status: SessionStatus;
  answered: number;
  total: number;
}

/** Ce que `GET /state` envoie sous `session` — le `CurrentSessionDto` du
 *  serveur, et PAS une `SessionInfo`.
 *
 *  🪤 L'objet est TOUJOURS là, même quand le candidat n'a ouvert aucune
 *  session : `_get_candidate_state` part d'un `CurrentSessionDto()` nu et ne
 *  le remplace que s'il trouve une session. Tester sa présence ne répond donc
 *  à rien — un `if (state.session)` passe toujours. La question qui a un sens
 *  est `session_id !== null`. `answered`/`total` valent 0, jamais `null`. */
export interface CurrentSession {
  session_id: string | null;
  session_index: number | null;
  status: SessionStatus | null;
  answered: number;
  total: number;
}

/** Réponse de POST /{token}/session — tirage NEUF, cartes de test only
 *  (jamais de warmup, cf. CONTRACT §3). */
export interface StartSessionResponse {
  session_id: string;
  session_index: number;
  cards: Card[];
}

/** Une carte ratée (ou imparfaite) présentée dans l'écran de revue —
 *  pédagogie « Tu as … / Il fallait … » (cf. maquette étape 4). Jamais
 *  expected_gesture/tolerance/rationale bruts (cf. CONTRACT §3 sanitize). */
export interface ReviewItem {
  fan_name: string | null;
  given_label: string;
  correct_label: string;
  why: string;
}

/** Réponse de POST /{token}/session/{id}/submit. */
export interface SessionSubmitResponse {
  score: number;
  threshold: number;
  reached: boolean;
  review: ReviewItem[];
}

/** État candidat enrichi — GET /{token}/state (ou GET /{token} enrichi, cf.
 *  CONTRACT §3). Sur-ensemble de `CandidateStatusResponse` : garde
 *  `status`/`first_name`/`has_profile`/`progress` (legacy, session 1 only)
 *  et ajoute la session courante + la maîtrise + la vérif technique. */
export interface CandidateStateResponse extends CandidateStatusResponse {
  session: CurrentSession;
  best_score: number | null;
  threshold: number;
  reached: boolean;
  device_class: DeviceClass | null;
  computer_confirmed: boolean;
  /** Nommé par le serveur, pas par la table : le DTO envoie
   *  `connection: {probe, passive}` là où les colonnes s'appellent
   *  `conn_probe_verdict`/`conn_passive_verdict`. L'objet est toujours
   *  présent (champ requis, pas d'`exclude_none`) — seules ses deux clés
   *  valent `null` tant que la sonde correspondante n'a pas tourné. */
  connection: {
    probe: ConnVerdict | null;
    passive: ConnVerdict | null;
  };
}

// ─── Config (singleton édité par Kayla — jamais en dur, CONTRACT §1d) ──────

export interface BaremePoints {
  correction: {
    correct_good_motif: number;
    correct_wrong_motif: number;
    correct_no_motif: number;
    reorient: number;
    discard: number;
    send: number;
  };
  clean: { send: number; reorient: number; correct: number; discard: number };
  discard: { discard: number; correct: number; reorient: number; send: number };
  reorient: { reorient: number; send: number; correct: number; discard: number };
  /** Geste qui déclenche 🚩 quand une carte dangereuse (fault/sale_2stage,
   *  expected_gesture != send) est envoyée telle quelle. Toujours "send". */
  dangerous_gesture: Gesture;
}

export interface EvalConfig {
  /** Score de session /100 requis pour "seuil atteint". */
  threshold: number;
  points: BaremePoints;
  /** Plafond de reprises — `null` = illimité. */
  max_sessions: number | null;
}

// ─── Fiche admin gradué (cf. maquette « Côté Kayla ») ──────────────────────

/** Carte graduée de la DERNIÈRE session, telle qu'affichée dans le tableau
 *  admin (colonnes Carte / Type · attendu / Son geste / points). */
export interface GradedCard {
  n: number;
  /** Type de carte lisible FR ("faute · invente", "propre", "time-waster",
   *  "vente 2 étages", …) — PAS la taxonomie serveur brute. */
  kind: string;
  /** Ce qu'il fallait faire, en clair ("Corrige + « elle invente »"). */
  expected_label: string;
  /** Ce que le candidat a fait, en clair ("Envoie tel quel"). */
  given_label: string;
  points: number;
  is_dangerous: boolean;
  note: string | null;
}

export type CriterionStatus = "ok" | "warn" | "bad";

export interface Criterion {
  status: CriterionStatus;
  label: string;
  /** `null` quand le critère n'a rien à préciser (`CriterionDto.detail`). */
  detail: string | null;
}

/**
 * Les critères de recrutement (💻📶📅).
 *
 * ⛔ Ils étaient QUATRE : le dernier (🎯 « le score ») notait le test de cartes
 * de La Régie. L'épreuve de vente qui l'a remplacé n'écrit AUCUN score — « la
 * conversation se lit, elle ne se note pas » —, si bien que le serveur lisait
 * 0 et déclarait « Seuil non atteint (0/80) » pour tout le monde. Le champ
 * part du contrat serveur dans la PR qui suit ; le cockpit cesse de le lire
 * d'abord, parce qu'il se déploie d'abord.
 */
export interface Criteria {
  computer: Criterion;
  connection: Criterion;
  availability: Criterion;
}

export interface Verdict {
  /** true SSI tous les critères sont `ok`. */
  recruitable: boolean;
  /** Ce qui manque quand `recruitable` est faux (vide sinon). */
  reason: string;
  suggestion: { cls: CriterionStatus; txt: string };
}

/** Un point de la courbe de maîtrise (une session). */
export interface SessionPoint {
  index: number;
  score: number;
  reached: boolean;
}

/**
 * `fault_learning` tel que le serveur le rend : deux séries parallèles, une
 * valeur par session, plus une tendance. C'est `score_candidate` qui le
 * construit (`scoring.py`).
 */
export interface FaultLearningDto {
  /** Cartes dangereuses envoyées, une entrée par session. */
  dangerous_sent: number[];
  false_positives: number[];
  /** Tendance décroissante de `dangerous_sent`. */
  improving: boolean;
}

/** Forme d'AFFICHAGE, dérivée du DTO ci-dessus — pas ce que le serveur envoie. */
export interface FaultLearning {
  label: string;
  progression: number[];
  improving: boolean;
}

/** Cellule de la matrice de couverture — liste triée desc par score
 *  (le serveur trie déjà, cf. contrat). */
export interface CoverageCandidate {
  candidate_id: string;
  first_name: string | null;
  score: number | null; // fraction 0..1, même convention que CandidateScore
}

export type CoverageMatrix = Record<DayKey, Record<SlotKey, CoverageCandidate[]>>;

/** Ce que `GET /coverage` envoie vraiment : une ENVELOPPE, pas la matrice.
 *
 *  🩸 Le cockpit a longtemps typé cette réponse `CoverageMatrix` et indexé
 *  dessus par jour. À l'exécution la valeur porte `days`/`slots`/`matrix` :
 *  chaque case sortait vide et le KPI annonçait 42 créneaux non couverts,
 *  quel que soit le vivier. Le mock rendait la matrice nue, donc il
 *  confirmait le mauvais type et aucun test ne pouvait rougir.
 *
 *  ⚠️ `slots` ne vaut PAS `SlotKey[]` : le serveur n'en produit que quatre
 *  (`a`→`d`) là où la grille en a six. C'est un retard du back, pas du
 *  cockpit — d'où `string[]` plutôt qu'une promesse qu'il ne tient pas. */
export interface CoverageResponse {
  days: string[];
  slots: string[];
  matrix: CoverageMatrix;
}

/** GET /items — l'admin n'a besoin en v1 que d'un compteur d'items actifs
 *  (pas d'éditeur complet). La devinette `active` OU `is_active` est
 *  tranchée : `ItemDto` envoie `active`, requis. */
export interface RegieTestItemSummary {
  id: string;
  active: boolean;
}

/* ── L'épreuve de vente ────────────────────────────────────────────────── */

/**
 * Ce que le candidat renvoie à la fin de l'épreuve. Le backend le range TEL
 * QUEL dans `regie_test_sessions.score_snapshot` — aucun re-calcul serveur,
 * et c'est voulu : la seule note mécanique est celle que le moteur de la
 * scène a déjà rendue, et la partie qui compte (ce qu'il a écrit) ne se note
 * pas du tout.
 */
export interface VenteCriterePayload {
  cle: string;
  libelle: string;
  /** null = il n'est jamais arrivé jusque-là. */
  reussi: boolean | null;
  note?: string;
}

/**
 * Un geste du candidat pendant l'epreuve, tel qu'il est rangé en base et relu
 * par la fiche admin. Repond a la demande de Kayla (18-08) : « pour chaque
 * etape, je veux voir s'il a execute une action script ou s'il a redige un
 * message, et dans ce cas quel message ».
 *
 * `source` est `null` pour ce qui n'a ni provenance ni chemin (ajouter,
 * ouvrir, rendre la main, les marques du chrono).
 */
export interface VenteGestePayload {
  /** L'etape de la scene ("file", "flow", "proposer", … ; "intro" pour le
   *  temps passé sur l'introduction). Les scènes passées en portent d'autres
   *  ("ultimatum", "tarifs", "menu", …). */
  etape: string;
  /** "ajouter" | "ouvrir" | "dire" | "lancer_flow" | "arreter_flow" |
   *  "transaction" | "rendre" — et "debut" / "fin", les marques du chrono de
   *  chaque étape (06-10-2026). Les scènes passées portent aussi "menu". */
  type: string;
  texte: string;
  /** La provenance d'un texte ("script", "script_modifie", "francis",
   *  "francis_modifie", "ecrit"), le chemin d'un flow ("a_faire", "menu") ou
   *  d'une vente ("vente", "francis") — null pour le reste. */
  source: string | null;
  a_ms: number;
  reussi: boolean;
}

/** Ce que la fiche admin lit d'une épreuve de vente déjà passée. */
export interface VenteResultat {
  criteres: VenteCriterePayload[];
  reussis: number;
  total: number;
  duree_secondes: number | null;
  ses_messages: string[];
  /** Optionnel : les candidatures passées AVANT le 18-08 n'ont pas le detail
   *  par etape. La fiche doit donc savoir s'en passer. */
  gestes?: VenteGestePayload[];
}

export interface VenteResultatPayload {
  /** Discriminant lu par la fiche admin — distingue une session « vente »
   *  d'un vieux `score_snapshot` de cartes de décision. */
  kind: "vente";
  criteres: VenteCriterePayload[];
  reussis: number;
  total: number;
  duree_secondes: number | null;
  /** Tout ce qu'il a écrit, dans l'ordre. La matière que Kayla lit. */
  ses_messages: string[];
  /** Le detail par etape — action script ou message redige, et lequel. */
  gestes: VenteGestePayload[];
}
